import React, { useState, useEffect } from "react";
import "./ManageCalendarPage.css";
import api from '../../api';

export default function ManageCalendarPage({ canEdit = false }) {
  const [calendarItems, setCalendarItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [newItem, setNewItem] = useState({
    title: "",
    team_id: "",
    post_date: "",
    status: "Scheduled",
  });
  const [viewMode, setViewMode] = useState("week"); // "week", "month", or "custom"
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [editData, setEditData] = useState({});
  const [editingField, setEditingField] = useState(null); // Track which field is being edited: {id, field}

  // Helper to get Monday of current week
  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  // Helper to get start/end of current week
  const getCurrentWeekRange = () => {
    const today = new Date();
    const monday = getMonday(today);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    return {
      start: formatDateForInput(monday.toISOString()),
      end: formatDateForInput(sunday.toISOString())
    };
  };

  // Helper to get start/end of current month
  const getCurrentMonthRange = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      start: formatDateForInput(firstDay.toISOString()),
      end: formatDateForInput(lastDay.toISOString())
    };
  };

  // Helper to format date for input (YYYY-MM-DD)
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to format date for display (avoiding timezone issues)
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    
    // Clean the date string - remove any time portion or timezone info
    let cleanDate = dateStr;
    if (typeof dateStr === 'string') {
      // Remove time portion if present (e.g., "2025-11-11T05:00:00.000Z" -> "2025-11-11")
      cleanDate = dateStr.split('T')[0].split(' ')[0];
      
      // If already in YYYY-MM-DD format, parse it directly
      if (cleanDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = cleanDate.split('-');
        // Format as MM/DD/YYYY for display
        return `${month}/${day}/${year}`;
      }
    }
    
    // Otherwise, try to parse and format using local date methods
    const date = new Date(cleanDate);
    if (isNaN(date.getTime())) return dateStr;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  };

  // Fetch calendar items and teams from backend on mount
  useEffect(() => {
    fetchCalendarItems();
    fetchTeams();
    // Set default to current week
    const today = new Date();
    const monday = getMonday(today);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    setDateRange({
      start: formatDateForInput(monday.toISOString()),
      end: formatDateForInput(sunday.toISOString())
    });
  }, []);

  // Update date range when view mode changes
  useEffect(() => {
    if (viewMode === "week") {
      const today = new Date();
      const monday = getMonday(today);
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      setDateRange({
        start: formatDateForInput(monday.toISOString()),
        end: formatDateForInput(sunday.toISOString())
      });
    } else if (viewMode === "month") {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setDateRange({
        start: formatDateForInput(firstDay.toISOString()),
        end: formatDateForInput(lastDay.toISOString())
      });
    }
    // For "custom", don't auto-update - user sets it manually
  }, [viewMode]);

  const fetchTeams = async () => {
    try {
      const res = await api.get('/teams');
      setTeams(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch teams:", err);
    }
  };

  const fetchCalendarItems = async () => {
    try {
      const res = await api.get('/calendar');
      setCalendarItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch calendar items:", err);
    }
  };

  // Filter calendar items by date range
  const filteredItems = calendarItems.filter((i) => {
    if (dateRange.start && dateRange.end) {
      const itemDate = new Date(i.post_date);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      // Set time to start/end of day for proper comparison
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      itemDate.setHours(0, 0, 0, 0);
      return itemDate >= startDate && itemDate <= endDate;
    }
    return true;
  });

  // --- Calendar Handlers ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newItem.title.trim()) {
      alert("Title is required");
      return;
    }

    if (!newItem.post_date) {
      alert("Post date is required");
      return;
    }

    try {
      const res = await api.post('/calendar', {
        title: newItem.title,
        team_id: newItem.team_id || null,
        post_date: newItem.post_date,
        status: newItem.status,
      });

      setCalendarItems([...calendarItems, res.data]);
      setNewItem({ title: "", team_id: "", post_date: "", status: "Scheduled" });
    } catch (err) {
      console.error("Failed to create calendar item:", err);
      alert(err.response?.data?.error || "Failed to create calendar item");
    }
  };

      const handleFieldEdit = (item, field) => {
        setEditingField({ id: item.id, field });
        // Ensure post_date is in YYYY-MM-DD format for the date input
        // Parse date string directly to avoid timezone issues
        const formatDateForInput = (dateStr) => {
          if (!dateStr) return '';
          // If already in YYYY-MM-DD format, use it directly
          if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
            return dateStr.split('T')[0]; // Remove time portion if present
          }
          // Otherwise, parse as local date (not UTC)
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return '';
          // Use local date methods to avoid timezone shift
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        const formattedDate = formatDateForInput(item.post_date);
        setEditData({ 
          ...item, 
          post_date: formattedDate 
        });
      };

      const handleFieldSave = async (itemId, field, value) => {
        const item = calendarItems.find(i => i.id === itemId);
        if (!item) return;

        // Helper function to format date to YYYY-MM-DD (avoiding timezone issues)
        const formatDate = (dateValue) => {
          if (!dateValue) return null;
          if (typeof dateValue === 'string') {
            // If it's already a string in YYYY-MM-DD format, use it directly
            if (dateValue.match(/^\d{4}-\d{2}-\d{2}/)) {
              return dateValue.split('T')[0]; // Take only the date part if it includes time
            }
            // Try to parse and format using local date methods (not UTC)
            const date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
            return dateValue;
          }
          if (dateValue instanceof Date) {
            // Use local date methods to avoid timezone shift
            const year = dateValue.getFullYear();
            const month = String(dateValue.getMonth() + 1).padStart(2, '0');
            const day = String(dateValue.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
          return dateValue;
        };

        // Prepare update data
        let updateData = {
          title: item.title,
          team_id: item.team_id || null,
          post_date: formatDate(item.post_date),
          status: item.status,
        };

        // Update the specific field
        if (field === 'post_date') {
          updateData.post_date = formatDate(value);
        } else if (field === 'team_id') {
          updateData.team_id = value ? parseInt(value) : null;
        } else {
          updateData[field] = value;
        }

        // Validate required fields
        if (!updateData.title || !updateData.title.trim()) {
          alert("Title is required");
          setEditingField(null);
          return;
        }

        if (!updateData.post_date) {
          alert("Post date is required");
          setEditingField(null);
          return;
        }

        try {
          console.log("📤 Sending update:", updateData);
          const res = await api.put(`/calendar/${itemId}`, updateData);
          setCalendarItems(calendarItems.map((i) => (i.id === itemId ? res.data : i)));
          setEditingField(null);
        } catch (err) {
          console.error("Failed to update calendar item:", err);
          console.error("Error response:", err.response?.data);
          alert(err.response?.data?.error || err.response?.data?.details || err.message || "Failed to update calendar item");
          setEditingField(null);
        }
      };

      const handleFieldCancel = () => {
        setEditingField(null);
        setEditData({});
      };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this calendar item?")) {
      return;
    }

    try {
      await api.delete(`/calendar/${id}`);
      setCalendarItems(calendarItems.filter((i) => i.id !== id));
    } catch (err) {
      console.error("Failed to delete calendar item:", err);
      alert(err.response?.data?.error || "Failed to delete calendar item");
    }
  };

  return (
    <div className="calendar-page">
      <h2>Manage Calendar</h2>

      {/* Add New Calendar Item Form */}
      {canEdit && (
      <form onSubmit={handleSubmit} className="calendar-form">
        <input
          type="text"
          placeholder="Title *"
          value={newItem.title}
          onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
          required
        />
        <select
          value={newItem.team_id}
          onChange={(e) => setNewItem({ ...newItem, team_id: e.target.value })}
        >
          <option value="">Select Team</option>
          {teams.map(team => (
            <option key={team.id} value={team.id}>{team.name}</option>
          ))}
        </select>
        <input
          type="date"
          placeholder="Post Date *"
          value={newItem.post_date}
          onChange={(e) => setNewItem({ ...newItem, post_date: e.target.value })}
          required
        />
        <select
          value={newItem.status}
          onChange={(e) => setNewItem({ ...newItem, status: e.target.value })}
        >
          <option value="Scheduled">Scheduled</option>
          <option value="Completed">Completed</option>
        </select>
        <button type="submit">Add Item</button>
      </form>
      )}

      {/* Date Range Filter */}
          <div className="calendar-filter">
            <h4>View Options</h4>
            <div className="filter-controls">
              <div className="view-mode-buttons">
                <button
                  type="button"
                  className={viewMode === "week" ? "active" : ""}
                  onClick={() => setViewMode("week")}
                >
                  Current Week
                </button>
                <button
                  type="button"
                  className={viewMode === "month" ? "active" : ""}
                  onClick={() => setViewMode("month")}
                >
                  Current Month
                </button>
                <button
                  type="button"
                  className={viewMode === "custom" ? "active" : ""}
                  onClick={() => setViewMode("custom")}
                >
                  Custom Range
                </button>
              </div>
              {viewMode === "custom" && (
                <div className="filter-inputs">
                  <label>
                    <span>Start:</span>
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    />
                  </label>
                  <label>
                    <span>End:</span>
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

      {/* Calendar Items Table */}
      <table className="calendar-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Team</th>
            <th>Post Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredItems.length === 0 ? (
            <tr>
              <td colSpan="5" style={{ textAlign: "center", padding: "2rem" }}>
                No calendar items found
              </td>
            </tr>
          ) : (
            filteredItems.map((i) => {
              const isEditingTitle = editingField?.id === i.id && editingField?.field === 'title';
              const isEditingDate = editingField?.id === i.id && editingField?.field === 'post_date';
              // Format date for display without timezone conversion
              const formatDateForInput = (dateStr) => {
                if (!dateStr) return '';
                // If already in YYYY-MM-DD format, use it directly
                if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
                  return dateStr.split('T')[0]; // Remove time portion if present
                }
                // Otherwise, parse as local date (not UTC)
                const date = new Date(dateStr);
                if (isNaN(date.getTime())) return '';
                // Use local date methods to avoid timezone shift
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              const formattedDate = formatDateForInput(i.post_date);

              return (
                <tr key={i.id}>
                  <td data-label="Title">
                    {isEditingTitle ? (
                      <input
                        type="text"
                        value={editData.title || ''}
                        onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                        onBlur={() => handleFieldSave(i.id, 'title', editData.title)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleFieldSave(i.id, 'title', editData.title);
                          } else if (e.key === 'Escape') {
                            handleFieldCancel();
                          }
                        }}
                        autoFocus
                        className="inline-edit-input"
                      />
                    ) : (
                      <span 
                        onClick={canEdit ? () => handleFieldEdit(i, 'title') : undefined}
                        className={canEdit ? "editable-field" : ""}
                        title={canEdit ? "Click to edit" : "View only"}
                        style={!canEdit ? { cursor: 'default' } : {}}
                      >
                        {i.title}
                      </span>
                    )}
                  </td>
                  <td data-label="Team">
                    {canEdit ? (
                      <select
                        value={i.team_id || ''}
                        onChange={(e) => handleFieldSave(i.id, 'team_id', e.target.value)}
                        className="inline-edit-select"
                      >
                        <option value="">-</option>
                        {teams.map(team => (
                          <option key={team.id} value={team.id}>{team.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span>{i.team_name || "-"}</span>
                    )}
                  </td>
                  <td data-label="Post Date">
                    {isEditingDate ? (
                      <input
                        type="date"
                        value={editData.post_date || formattedDate}
                        onChange={(e) => setEditData({ ...editData, post_date: e.target.value })}
                        onBlur={() => handleFieldSave(i.id, 'post_date', editData.post_date)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleFieldSave(i.id, 'post_date', editData.post_date);
                          } else if (e.key === 'Escape') {
                            handleFieldCancel();
                          }
                        }}
                        autoFocus
                        className="inline-edit-input"
                      />
                    ) : (
                      <span 
                        onClick={canEdit ? () => handleFieldEdit(i, 'post_date') : undefined}
                        className={canEdit ? "editable-field" : ""}
                        title={canEdit ? "Click to edit" : "View only"}
                        style={!canEdit ? { cursor: 'default' } : {}}
                      >
                        {formatDateForDisplay(i.post_date)}
                      </span>
                    )}
                  </td>
                  <td data-label="Status">
                    {canEdit ? (
                      <select
                        value={i.status}
                        onChange={(e) => handleFieldSave(i.id, 'status', e.target.value)}
                        className="inline-edit-select"
                      >
                        <option value="Scheduled">Scheduled</option>
                        <option value="Completed">Completed</option>
                      </select>
                    ) : (
                      <span>{i.status}</span>
                    )}
                  </td>
                  <td>
                    {canEdit && (
                      <button className="delete-btn" onClick={() => handleDelete(i.id)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

